/**
 * Creates a pair of `focus` event handlers for the sentinel-based focus-trap
 * pattern used in modal panels (e.g. mobile menus and mobile dropdowns).
 *
 * Two invisible sentinel elements bookend the panel's focusable content:
 *
 * ```
 * <span class="sentinelStart" tabindex="0" @focus=${handlers.start}>
 * <!-- real focusable content -->
 * <span class="sentinelEnd"   tabindex="0" @focus=${handlers.end}>
 * ```
 *
 * @param host      - The element that owns the panel. Used to determine
 *                    whether focus is arriving from inside or outside via
 *                    `FocusEvent.relatedTarget`.
 * @param focusFirst - Callback that moves focus to the first real focusable
 *                    element inside the panel.
 * @param focusLast  - Callback that moves focus to the last real focusable
 *                    element inside the panel.
 *
 * @returns An object with `start` and `end` handlers to bind to the sentinels.
 */
export function createFocusTrapSentinels(
  focusFirst: () => void,
  focusLast: () => void,
): {
  start: (event: FocusEvent) => void
  end: (event: FocusEvent) => void
} {
  return {
    start(): void {
      focusLast()
    },
    end(): void {
      focusFirst()
    },
  }
}
